import BigNumber from 'bignumber.js';
import styled from 'styled-components';
import { useState } from 'react';
import { rgba } from 'polished';

import DepositIcon from '#/assets/DepositIcon';
import InProgressIcon from '#/assets/InProgressIcon';

import { c7239C4, cFFFFFF } from '#/colors';

import { Error as ErrorMsg } from '#/components/common/alert';
import toast from '#/components/common/toast';
import ToastTitle from '#/components/common/toast/Title';

import { getRpcError } from '#/features/wallet/ethereum';
import { useWalletState } from '#/features/wallet/wallet-context';
import { logException } from '#/features/logging/logging';
import StarknetWallet from '#/features/starknet-wallet/wallet';
import { useSystemConfig } from '#/features/system/system-config-context';
import { useStarknetProvider } from '#/features/starknet-wallet/provider';
import { useBuildStarknetTxUrl } from '#/features/starknet';

import { Maybe } from '#/utils/types';
import { waitForTransaction } from '#/utils/starknet';

const AMOUNT_OPTIONS = [
  { value: BigNumber(1_000), label: '1K', width: '50px' },
  { value: BigNumber(10_000), label: '10K', width: '50px' },
  { value: BigNumber(100_000), label: '100K', width: '50px' },
  { value: BigNumber(1_000_000), label: '1M', width: '50px' },
];

export default function QuickWithdrawal() {
  const config = useSystemConfig();
  const starknetProvider = useStarknetProvider();
  const buildStarknetTxUrl = useBuildStarknetTxUrl();
  const [walletState] = useWalletState();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [amount, setAmount] = useState('100000');

  const isDisabled = walletState.starknetAddress == null;

  const token = config.getToken('USDC');

  /**
   * Logs an error and sets the error message
   */
  function handleError(error: Error) {
    const { cause } = error;

    const rpcErrorMessage = getRpcError(cause)?.message;
    const isException = rpcErrorMessage == null;
    if (isException) logException(error);

    const step = error.message;
    const reason =
      rpcErrorMessage ?? (cause as Maybe<Error>)?.message ?? String(cause);

    const message = `${step}: ${reason}`;
    setErrorMsg(message);
  }

  const requestWithdrawal = (value: string) => {
    (async () => {
      setErrorMsg('');
      setIsLoading(true);

      const response = await StarknetWallet.executeTransaction(
        starknetProvider,
        {
          contractAddress: config.paraclear.address,
          entrypoint: 'withdraw',
          calldata: [token.l2TokenAddress, value],
        },
      );

      const txHash = response.transaction_hash;

      notifyTransactionSent(txHash, buildStarknetTxUrl(txHash));
      await waitForTransaction(starknetProvider, txHash);
      notifyTransactionAccepted(txHash, buildStarknetTxUrl(txHash));
    })()
      .catch((err) => {
        handleError(new Error('Error requesting withdrawal', { cause: err }));
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleWithdrawal = () => {
    requestWithdrawal(toQuantums(BigNumber(amount), config.paraclear.decimals));
  };

  const isValid = BigNumber(amount).isFinite();

  return (
    <div css="display: grid; gap: 16px; position: relative;">
      <div css="display: grid; gap: 8px;">
        <Input
          step="0.01"
          name="amount"
          value={amount}
          disabled={isLoading}
          onChange={(e) => setAmount(e.target.value)}
        />

        <AmountsContainer>
          {AMOUNT_OPTIONS.map(({ value, label }) => (
            <AmountButton
              disabled={isLoading}
              onClick={() => setAmount(value.toFixed())}
              key={label}
            >
              {label}
            </AmountButton>
          ))}
        </AmountsContainer>
      </div>
      <div css="display:grid;">
        <Button onClick={handleWithdrawal} disabled={isLoading || !isValid}>
          Withdraw
        </Button>
      </div>
      {errorMsg !== '' && <ErrorMsg>{errorMsg}</ErrorMsg>}
      {isDisabled && <DisabledOverlay />}
      {isLoading && <LoadingOverlay />}
    </div>
  );
}

function notifyTransactionAccepted(txHash: string, txUrl: string) {
  toast.success(
    <>
      <ToastTitle>Transaction Accepted</ToastTitle>
      <span css="font-family: 'Roboto Mono', monospace;">{txHash}</span>
      <br />
      <a
        href={txUrl}
        rel="nofollow noopener"
        target="_blank"
        css="text-decoration: underline;"
      >
        View on Explorer
      </a>
    </>,
  );
}

function notifyTransactionSent(txHash: string, txUrl: string) {
  toast.info(
    <>
      <ToastTitle>Transaction sent</ToastTitle>
      <span css="font-family: 'Roboto Mono', monospace;">{txHash}</span>
      <br />
      <a
        href={txUrl}
        rel="nofollow noopener"
        target="_blank"
        css="text-decoration: underline;"
      >
        View on Explorer
      </a>{' '}
    </>,
    { icon: <DepositIcon size={19} /> },
  );
}

/**
 * Convert to quantums rounding final number down.
 *
 * @param amount Amount in human numbers
 * @param decimals How many decimals the target contract works with
 * @returns Quantum value
 *
 * @example
 *  toQuantums(100, 5) === "10000000";
 *  toQuantums(10, 5) === "1000000";
 *  toQuantums(1, 5) === "100000";
 *  toQuantums(0.1, 5) === "10000";
 *  toQuantums(0.01, 5) === "1000";
 *  toQuantums(0.001, 5) === "100";
 *  toQuantums(0.0001, 5) === "10";
 *  toQuantums(0.00001, 5) === "1";
 *  toQuantums(0.000001, 5) === "0";
 */
function toQuantums(amount: BigNumber, decimals: number): string {
  const bnQuantums = amount.multipliedBy(
    BigNumber(10).exponentiatedBy(decimals),
  );
  return bnQuantums.integerValue(BigNumber.ROUND_FLOOR).toString();
}

const Input = styled.input.attrs({ type: 'text' })`
  height: 40px;
  width: 100%;
  border: 1px solid #2a2a4d;
  border-radius: 5px;
  background-color: #080820;
  color: #ffffff;
  padding: 10px 12px;
  font-family: 'Roboto Mono', monospace;
`;

const Button = styled.button.attrs({ type: 'button' })`
  background-color: ${c7239C4};
  border-radius: 5px;
  border: none;
  color: ${cFFFFFF};
  font-size: 0.875rem;
  font-weight: 500;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;

  &:disabled {
    color: ${cFFFFFF}AA;
    cursor: not-allowed;
  }
`;

const AmountButton = styled.button.attrs({ type: 'button' })`
  background-color: #0b0b20;
  border: none;
  border-radius: 3px;
  color: ${rgba('#FFFFFF', 0.7)};
  font-family: 'Roboto Mono', monospace;
  font-size: 12px;
  font-weight: 500;
  height: 19px;
  text-align: center;
  width: 100%;
`;

const AmountsContainer = styled.div`
  display: grid;
  grid-template-columns: ${AMOUNT_OPTIONS.map((opt) => opt.width).join(' ')};
  height: 20px;
  gap: 4px;
`;

const DisabledOverlay = styled.div`
  background-color: ${rgba('#211A3C', 0.7)};
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const LoadingOverlay = styled(DisabledOverlay).attrs({
  children: <InProgressIcon size={22} />,
})``;
